import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { DEFAULT_FILTERS, type CatalogFilters } from '@/entities/catalog';
import { useMyBoards } from '@/entities/collab';
import { useMyDatabases } from '@/entities/database';
import { useMyDocuments } from '@/entities/document';
import { useViewer } from '@/entities/viewer';
import { useWorkspaces } from '@/entities/workspace';
import { HubNav } from '@/features/hub-nav';
import { Combobox, ErrorState, LoadingState } from '@/shared/ui';

import { HomeDocuments } from './HomeDocuments';
import { HomeGroups } from './HomeGroups';
import styles from './HomeDashboard.module.css';
import { useHomePlaces } from '@/entities/places';

type Scope = 'any' | 'personal' | 'team';

/**
 * `/app/browse/notes` and `/app/browse/groups`: the complete list with a search and the workspace
 * kind. The same matching as the Home sections (`?q=` carries the text from the quick results).
 */
export function NoteGroupCategory({ category }: { category: 'documents' | 'groups' }) {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [space, setSpace] = useState<Scope>('any');
  const viewer = useViewer();
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const workspaces = useWorkspaces();
  const boards = useMyBoards({ limit: 1000 });
  const databases = useMyDatabases();
  const documents = useMyDocuments();
  const places = useHomePlaces(workspaces.data ?? []);
  const filters: CatalogFilters = { ...DEFAULT_FILTERS, space };
  const failed = [workspaces, boards, databases, documents].find((q) => q.isError);
  if (failed) return <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />;
  if (workspaces.isPending || boards.isPending || databases.isPending || documents.isPending) {
    return <LoadingState />;
  }
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          {t(category === 'documents' ? 'documents.title' : 'groups.sectionTitle')}
        </h1>
      </header>
      <HubNav />
      <div className={styles.toolbar} role="search">
        <input
          type="search"
          value={query}
          aria-label={t('home.search')}
          placeholder={t('home.search')}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <Combobox
          label={t('home.scope')}
          value={space}
          size="sm"
          searchable={false}
          options={[
            { value: 'any', label: t('home.scopeAll') },
            { value: 'personal', label: t('home.scopePersonal') },
            { value: 'team', label: t('home.scopeTeam') },
          ]}
          onChange={(next) => {
            setSpace(next === 'personal' || next === 'team' ? next : 'any');
          }}
        />
      </div>
      {category === 'documents' ? (
        <HomeDocuments
          full
          documents={documents.data ?? []}
          places={places}
          query={query}
          filters={filters}
          sort="updated"
          userId={userId}
          locale={i18n.language}
        />
      ) : (
        <HomeGroups
          full
          places={places}
          boards={boards.data ?? []}
          databases={databases.data ?? []}
          documents={documents.data ?? []}
          query={query}
          filters={filters}
          userId={userId}
        />
      )}
    </div>
  );
}
